import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Trash2, Edit, Plus, ArrowUp, ArrowDown, ExternalLink } from "lucide-react";
import ImageUploader from "@/components/admin/ImageUploader";
import LandingContentEditor from "@/components/admin/LandingContentEditor";

interface LagoaItem {
  id: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  neighborhood: string;
  bedrooms: number | null;
  suites: number | null;
  garage_spots: number | null;
  area_m2: number | null;
  price: number | null;
  mobiliado: boolean;
  varanda: boolean;
  vista_lagoa: boolean;
  features: string[] | null;
  image_url: string | null;
  listing_id: string | null;
  listing_url: string | null;
  whatsapp_message: string | null;
  is_featured: boolean;
  is_active: boolean;
  display_order: number;
}

const NEIGHBORHOODS = [
  "Lagoa",
  "Ipanema",
  "Copacabana",
  "Leblon",
  "Botafogo",
  "Flamengo",
  "Laranjeiras",
  "Centro do Rio",
  "Glória",
  "Jardim Botânico",
  "Gávea",
  "Barra da Tijuca",
  "Recreio dos Bandeirantes",
];

type FormState = Omit<LagoaItem, "id" | "features"> & { featuresText: string };

const empty = (): FormState => ({
  title: "",
  subtitle: "",
  description: "",
  neighborhood: "Lagoa",
  bedrooms: null,
  suites: null,
  garage_spots: null,
  area_m2: null,
  price: null,
  mobiliado: false,
  varanda: true,
  vista_lagoa: true,
  featuresText: "",
  image_url: "",
  listing_id: null,
  listing_url: "",
  whatsapp_message: "",
  is_featured: false,
  is_active: true,
  display_order: 0,
});

const LagoaLandingAdmin = () => {
  const { toast } = useToast();
  const [items, setItems] = useState<LagoaItem[]>([]);
  const [form, setForm] = useState<FormState>(empty());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const { data, error } = await (supabase as any)
      .from("lagoa_landing_items")
      .select("*")
      .order("display_order", { ascending: true })
      .order("created_at", { ascending: false });
    if (error) {
      toast({ title: "Erro ao carregar", description: error.message, variant: "destructive" });
      return;
    }
    setItems((data as LagoaItem[]) || []);
  };

  useEffect(() => {
    load();
  }, []);

  const startEdit = (item: LagoaItem) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      subtitle: item.subtitle || "",
      description: item.description || "",
      neighborhood: item.neighborhood || "Lagoa",
      bedrooms: item.bedrooms,
      suites: item.suites,
      garage_spots: item.garage_spots,
      area_m2: item.area_m2,
      price: item.price,
      mobiliado: item.mobiliado,
      varanda: item.varanda,
      vista_lagoa: item.vista_lagoa,
      featuresText: (item.features || []).join("\n"),
      image_url: item.image_url || "",
      listing_id: item.listing_id,
      listing_url: item.listing_url || "",
      whatsapp_message: item.whatsapp_message || "",
      is_featured: item.is_featured,
      is_active: item.is_active,
      display_order: item.display_order,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const reset = () => {
    setForm(empty());
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      title: form.title,
      subtitle: form.subtitle || null,
      description: form.description || null,
      neighborhood: form.neighborhood || "Lagoa",
      bedrooms: form.bedrooms,
      suites: form.suites,
      garage_spots: form.garage_spots,
      area_m2: form.area_m2,
      price: form.price,
      mobiliado: form.mobiliado,
      varanda: form.varanda,
      vista_lagoa: form.vista_lagoa,
      features: form.featuresText.split("\n").map((s) => s.trim()).filter(Boolean),
      image_url: form.image_url || null,
      listing_id: form.listing_id || null,
      listing_url: form.listing_url || null,
      whatsapp_message: form.whatsapp_message || null,
      is_featured: form.is_featured,
      is_active: form.is_active,
      display_order: form.display_order || 0,
    };

    const q = editingId
      ? (supabase as any).from("lagoa_landing_items").update(payload).eq("id", editingId)
      : (supabase as any).from("lagoa_landing_items").insert(payload);
    const { error } = await q;
    setLoading(false);
    if (error) {
      toast({ title: "Erro ao salvar", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: editingId ? "Item atualizado" : "Item criado" });
    reset();
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Excluir este item?")) return;
    const { error } = await (supabase as any).from("lagoa_landing_items").delete().eq("id", id);
    if (error) {
      toast({ title: "Erro", description: error.message, variant: "destructive" });
      return;
    }
    load();
  };

  const move = async (id: string, delta: number) => {
    const item = items.find((i) => i.id === id);
    if (!item) return;
    const newOrder = (item.display_order || 0) + delta;
    await (supabase as any).from("lagoa_landing_items").update({ display_order: newOrder }).eq("id", id);
    load();
  };

  const num = (v: string): number | null => (v === "" ? null : Number(v));

  return (
    <div className="space-y-6">
      <LandingContentEditor />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
        <form onSubmit={handleSubmit} className="bg-card rounded-lg p-6 shadow-lg border border-border space-y-3">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-display text-lg font-bold text-foreground">
              {editingId ? "Editar item da Landing" : "Novo item da Landing"}
            </h2>
            <Button asChild variant="link" size="sm">
              <a href="/lagoa/apartamentos-exclusivos-vista-lagoa-rodrigo-de-freitas-rj" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3 w-3 mr-1" /> Ver landing
              </a>
            </Button>
          </div>

          <div>
            <Label>Título *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          </div>
          <div>
            <Label>Subtítulo (ex: 230m² | Mobiliado | Vista Panorâmica)</Label>
            <Input value={form.subtitle || ""} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} />
          </div>
          <div>
            <Label>Bairro *</Label>
            <select
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
              value={form.neighborhood}
              onChange={(e) => setForm({ ...form, neighborhood: e.target.value })}
              required
            >
              {NEIGHBORHOODS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Descrição curta</Label>
            <Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div><Label>Quartos</Label><Input type="number" value={form.bedrooms ?? ""} onChange={(e) => setForm({ ...form, bedrooms: num(e.target.value) })} /></div>
            <div><Label>Suítes</Label><Input type="number" value={form.suites ?? ""} onChange={(e) => setForm({ ...form, suites: num(e.target.value) })} /></div>
            <div><Label>Vagas</Label><Input type="number" value={form.garage_spots ?? ""} onChange={(e) => setForm({ ...form, garage_spots: num(e.target.value) })} /></div>
            <div><Label>Área (m²)</Label><Input type="number" value={form.area_m2 ?? ""} onChange={(e) => setForm({ ...form, area_m2: num(e.target.value) })} /></div>
            <div className="col-span-2"><Label>Preço (R$)</Label><Input type="number" value={form.price ?? ""} onChange={(e) => setForm({ ...form, price: num(e.target.value) })} /></div>
          </div>

          <div className="flex gap-4 flex-wrap">
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.mobiliado} onCheckedChange={(v) => setForm({ ...form, mobiliado: !!v })} /> Mobiliado</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.varanda} onCheckedChange={(v) => setForm({ ...form, varanda: !!v })} /> Varanda</label>
            <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.vista_lagoa} onCheckedChange={(v) => setForm({ ...form, vista_lagoa: !!v })} /> Vista Lagoa</label>
          </div>

          <div>
            <Label>Destaques (um por linha)</Label>
            <Textarea rows={4} placeholder="Vista definitiva&#10;Mobiliado&#10;Alto padrão" value={form.featuresText} onChange={(e) => setForm({ ...form, featuresText: e.target.value })} />
          </div>

          <div>
            <Label>Imagem principal (URL)</Label>
            <Input value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." />
            <div className="mt-2">
              <ImageUploader
                images={form.image_url ? [form.image_url] : []}
                onChange={(imgs) => setForm({ ...form, image_url: imgs[imgs.length - 1] || "" })}
              />
            </div>
          </div>

          <div>
            <Label>Link "Ver detalhes" (URL do imóvel)</Label>
            <Input value={form.listing_url || ""} onChange={(e) => setForm({ ...form, listing_url: e.target.value })} placeholder="/lagoa/apartamento-..." />
          </div>

          <div>
            <Label>Mensagem WhatsApp customizada (opcional)</Label>
            <Input value={form.whatsapp_message || ""} onChange={(e) => setForm({ ...form, whatsapp_message: e.target.value })} placeholder="Olá! Tenho interesse no apartamento de 230m² na Lagoa..." />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Ordem</Label>
              <Input type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: Number(e.target.value) })} />
            </div>
            <div className="flex flex-col gap-2 pt-6">
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: !!v })} /> Destaque Premium</label>
              <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: !!v })} /> Ativo</label>
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={loading} className="flex-1">
              <Plus className="h-4 w-4 mr-1" /> {editingId ? "Salvar" : "Criar"}
            </Button>
            {editingId && (
              <Button type="button" variant="outline" onClick={reset}>Cancelar</Button>
            )}
          </div>
        </form>
      </div>

      <div className="lg:col-span-2 space-y-3">
        <h3 className="font-display text-lg font-bold">Itens cadastrados ({items.length})</h3>
        {items.length === 0 && (
          <p className="text-muted-foreground text-sm">Nenhum item cadastrado ainda. Adicione o primeiro apartamento no formulário ao lado.</p>
        )}
        {items.map((item) => (
          <div key={item.id} className="bg-card border border-border rounded-lg p-4 flex gap-4">
            {item.image_url && <img src={item.image_url} alt={item.title} className="w-24 h-24 object-cover rounded" />}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                {item.is_featured && <span className="text-xs bg-gold text-navy px-2 py-0.5 rounded font-semibold">DESTAQUE</span>}
                {!item.is_active && <span className="text-xs bg-muted px-2 py-0.5 rounded">INATIVO</span>}
                <span className="text-xs bg-navy/10 text-navy px-2 py-0.5 rounded font-medium">{item.neighborhood || "Lagoa"}</span>
                <span className="text-xs text-muted-foreground">ordem: {item.display_order}</span>
              </div>
              <h4 className="font-semibold">{item.title}</h4>
              <p className="text-sm text-muted-foreground">{item.subtitle}</p>
              <p className="text-sm">
                {item.bedrooms && `${item.bedrooms}q`} {item.garage_spots && `· ${item.garage_spots} vagas`} {item.area_m2 && `· ${item.area_m2}m²`} {item.price && `· R$ ${item.price.toLocaleString("pt-BR")}`}
              </p>
            </div>
            <div className="flex flex-col gap-1">
              <Button size="sm" variant="ghost" onClick={() => move(item.id, -1)}><ArrowUp className="h-3 w-3" /></Button>
              <Button size="sm" variant="ghost" onClick={() => move(item.id, 1)}><ArrowDown className="h-3 w-3" /></Button>
              <Button size="sm" variant="outline" onClick={() => startEdit(item)}><Edit className="h-3 w-3" /></Button>
              <Button size="sm" variant="destructive" onClick={() => remove(item.id)}><Trash2 className="h-3 w-3" /></Button>
            </div>
          </div>
        ))}
      </div>
      </div>
    </div>
  );
};

export default LagoaLandingAdmin;
